import { whenMarketingConsent } from '@nulo/cookiehub';
import { isMobileViewport, klaviyoEmailFormTracking } from '@nulo/gtm';
import {
  buildStepOneProfile,
  buildStepTwoProperties,
  identifyProfile,
  subscribeToList,
  updateProfileProperties,
} from '@nulo/klaviyo';

const SESSION_EMAIL = 'klaviyoSubscriberEmail';
const SESSION_STEP = 'emailOptStep';

/**
 * @typedef {Object} EmailOptI18n
 * @property {Record<string, string>} [errors]
 * @property {Record<string, string>} [stages]
 * @property {string} [pet_stage_placeholder]
 * @property {string} [submitting]
 * @property {string} [klaviyo_unconfigured]
 */

/**
 * @returns {boolean}
 */
function isMobileDevice() {
  if (navigator.userAgentData?.mobile) return true;

  const userAgent = navigator.userAgent || navigator.vendor || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}

/**
 * @extends {HTMLElement}
 */
class EmailOptForm extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string | null} */
  #subscriberEmail = null;

  /** @type {EmailOptI18n} */
  #i18n = {};

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.#i18n = this.#parseI18n();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initSelectFilledState(signal);
    this.#initPetBlocks(signal);
    this.#bindPetTypeStageSync(signal);
    this.#bindForms(signal);

    const petBlocks = this.querySelector('[data-pet-blocks]');
    if (petBlocks instanceof HTMLElement) {
      this.#renumberPetBlocks(petBlocks);
    }

    this.#restoreStep();
    this.#syncSelectFilledState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @returns {boolean}
   */
  #isConfigured() {
    return this.dataset.configured === 'true';
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  #guardConfigured(form) {
    if (this.#isConfigured()) return true;

    this.#renderApiError(form, this.#i18n.klaviyo_unconfigured || '');
    return false;
  }

  /**
   * @returns {EmailOptI18n}
   */
  #parseI18n() {
    const el = this.querySelector('[data-i18n]');
    if (!el?.textContent) return {};

    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #errorMessage(key) {
    const errors = this.#i18n.errors || {};

    if (key === 'email') return errors.email_required || '';
    if (key === 'petType') return errors.pet_type_required || '';
    if (key === 'shopPlace') return errors.shop_place_required || '';
    if (key === 'zipCode') return errors.zip_code_required || '';

    if (/^pet\d+_Name$/.test(key)) return errors.pet_name_required || '';
    if (/^pet\d+_Type$/.test(key)) return errors.pet_type_field_required || '';
    if (/^pet\d+_Stage$/.test(key)) return errors.pet_stage_required || '';
    if (/^pet\d+_Birthday$/.test(key)) return errors.pet_birthday_required || '';

    return '';
  }

  /**
   * @param {AbortSignal} signal
   */
  #initSelectFilledState(signal) {
    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        target.classList.toggle('email-opt__select--filled', Boolean(target.value));
      },
      { signal }
    );
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindForms(signal) {
    const stepOneForm = this.querySelector('[data-step-one-form]');
    stepOneForm?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#handleStepOneSubmit(stepOneForm);
      },
      { signal }
    );

    const stepTwoForm = this.querySelector('[data-step-two-form]');
    stepTwoForm?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#handleStepTwoSubmit(stepTwoForm);
      },
      { signal }
    );

    const skipBtn = this.querySelector('[data-email-opt-skip]');
    skipBtn?.addEventListener('click', () => this.#goToThankYou(), { signal });
  }

  #restoreStep() {
    const savedStep = sessionStorage.getItem(SESSION_STEP);
    const savedEmail = sessionStorage.getItem(SESSION_EMAIL);

    if (savedStep === '2' && savedEmail) {
      this.#subscriberEmail = savedEmail;
      this.#showStep('2');
    }
  }

  #syncSelectFilledState() {
    for (const select of this.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      select.classList.toggle('email-opt__select--filled', Boolean(select.value));
    }
  }

  /**
   * @param {'1' | '2'} step
   */
  #showStep(step) {
    sessionStorage.setItem(SESSION_STEP, step);

    for (const panel of this.querySelectorAll('[data-email-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.emailPanel !== step;
    }

    const wrapper = this.querySelector('[data-forms-wrapper]');
    if (wrapper instanceof HTMLElement) {
      wrapper.classList.toggle('email-opt__forms-wrapper--step-one', step === '1');
    }

    this.dataset.currentStep = step;
  }

  /**
   * @param {AbortSignal} signal
   */
  #initPetBlocks(signal) {
    const container = this.querySelector('[data-pet-blocks]');
    if (!container) return;

    container.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const addBtn = target.closest('[data-add-pet]');
        if (addBtn instanceof HTMLElement && container.contains(addBtn)) {
          this.#addPetBlock();
          return;
        }

        const removeBtn = target.closest('[data-remove-pet]');
        if (!(removeBtn instanceof HTMLElement)) return;

        const block = removeBtn.closest('[data-pet-block]');
        if (!block || !container.contains(block)) return;

        const blocks = container.querySelectorAll('[data-pet-block]');
        if (blocks.length <= 1) return;

        block.remove();
        this.#renumberPetBlocks(container);
      },
      { signal }
    );
  }

  /**
   * @param {HTMLElement} container
   */
  #renumberPetBlocks(container) {
    const blocks = container.querySelectorAll('[data-pet-block]');

    blocks.forEach((block, index) => {
      const petIndex = index + 1;
      block.dataset.petIndex = String(petIndex);

      for (const field of block.querySelectorAll('[data-pet-field]')) {
        if (!(field instanceof HTMLElement)) continue;
        const fieldName = field.dataset.petField;
        if (!fieldName) continue;

        const name = `pet${petIndex}_${fieldName}`;
        field.setAttribute('name', name);
        field.id = `email-opt-pet${petIndex}-${fieldName}`;

        const errorEl = field
          .closest('.email-opt__field')
          ?.querySelector('[data-field-error]');
        if (errorEl instanceof HTMLElement) {
          errorEl.setAttribute('data-field-error', name);
        }

        const label = field.closest('.email-opt__field')?.querySelector('label');
        if (label instanceof HTMLLabelElement && field.id) {
          label.htmlFor = field.id;
        }
      }

      const removeBtn = block.querySelector('[data-remove-pet]');
      const addBtn = block.querySelector('[data-add-pet]');
      if (removeBtn instanceof HTMLElement) {
        removeBtn.hidden = blocks.length <= 1;
      }
      if (addBtn instanceof HTMLElement) {
        addBtn.hidden = index !== blocks.length - 1;
      }
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindPetTypeStageSync(signal) {
    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (target.dataset.petField !== 'Type') return;

        const block = target.closest('[data-pet-block]');
        if (!block) return;

        const stageSelect = block.querySelector('[data-pet-field="Stage"]');
        if (!(stageSelect instanceof HTMLSelectElement)) return;

        this.#populateStageOptions(stageSelect, target.value);
      },
      { signal }
    );
  }

  /**
   * @param {HTMLSelectElement} stageSelect
   * @param {string} petType
   */
  #populateStageOptions(stageSelect, petType) {
    const previous = stageSelect.value;
    stageSelect.innerHTML = '';

    const stages = this.#i18n.stages || {};

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent =
      this.#i18n.pet_stage_placeholder || "What is your pet's life stage?";
    stageSelect.appendChild(placeholder);

    /** @type {{ value: string, label: string }[]} */
    let options = [];

    if (petType === 'Dog') {
      options = [
        { value: 'Puppy', label: stages.puppy || 'Puppy' },
        { value: 'Adult', label: stages.adult || 'Adult' },
        { value: 'Senior', label: stages.senior || 'Senior' },
      ];
    } else if (petType === 'Cat') {
      options = [
        { value: 'Kitten', label: stages.kitten || 'Kitten' },
        { value: 'Adult', label: stages.adult || 'Adult' },
        { value: 'Senior', label: stages.senior || 'Senior' },
      ];
    } else {
      options = [
        { value: 'Kitten', label: stages.kitten || 'Kitten' },
        { value: 'Puppy', label: stages.puppy || 'Puppy' },
        { value: 'Adult', label: stages.adult || 'Adult' },
        { value: 'Senior', label: stages.senior || 'Senior' },
      ];
    }

    for (const option of options) {
      const el = document.createElement('option');
      el.value = option.value;
      el.textContent = option.label;
      stageSelect.appendChild(el);
    }

    stageSelect.classList.toggle('email-opt__select--filled', false);

    if (options.some((option) => option.value === previous)) {
      stageSelect.value = previous;
      stageSelect.classList.toggle('email-opt__select--filled', Boolean(previous));
    }
  }

  #addPetBlock() {
    const container = this.querySelector('[data-pet-blocks]');
    const template = this.querySelector('[data-pet-block-template]');
    if (!(container instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    const clone = template.content.firstElementChild?.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    container.appendChild(clone);
    this.#renumberPetBlocks(container);
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #handleStepOneSubmit(form) {
    this.#clearErrors(form);

    if (!this.#guardConfigured(form)) return;

    const email = form.querySelector('[name="email"]')?.value?.toString().trim() || '';
    const petType = form.querySelector('[name="petType"]')?.value?.trim() || '';
    const shopPlaces = [...form.querySelectorAll('[name="shopPlace"]:checked')]
      .map((input) => input.value)
      .filter(Boolean);

    /** @type {Record<string, string>} */
    const errors = {};
    const err = this.#i18n.errors || {};

    if (!email) {
      errors.email = err.email_required || '';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = err.email_invalid || '';
    }

    if (!petType) {
      errors.petType = err.pet_type_required || '';
    }

    if (!shopPlaces.length) {
      errors.shopPlace = err.shop_place_required || '';
    }

    if (Object.keys(errors).length > 0) {
      this.#renderErrors(form, errors);
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    this.#setSubmitting(submitButton, true);

    const deviceType = isMobileDevice() ? 'mobile' : 'desktop';
    const sourceType = this.dataset.sourceType || 'Custom Landing Page';
    const sourceFormLocale = this.dataset.sourceFormLocale || 'EU/EN';
    const listId = this.dataset.listId || '';

    const profile = buildStepOneProfile({
      email,
      petType,
      shopPlace: shopPlaces.join(', '),
      sourceType,
      deviceType,
      sourceFormLocale,
    });

    try {
      const consented = await whenMarketingConsent();
      if (!consented) {
        throw new Error(err.consent_required || this.#i18n.errors?.generic || '');
      }

      await identifyProfile(profile);
      await subscribeToList({
        listId,
        email,
        customSource: sourceType,
      });

      this.#subscriberEmail = email;
      sessionStorage.setItem(SESSION_EMAIL, email);

      klaviyoEmailFormTracking('email_subscribe', isMobileViewport());

      this.#showStep('2');
    } catch (error) {
      this.#renderApiError(
        form,
        error instanceof Error ? error.message : this.#i18n.errors?.generic || ''
      );
    } finally {
      this.#setSubmitting(submitButton, false);
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  async #handleStepTwoSubmit(form) {
    this.#clearErrors(form);

    if (!this.#guardConfigured(form)) return;

    const email = this.#subscriberEmail || sessionStorage.getItem(SESSION_EMAIL);
    if (!email) {
      this.#renderApiError(form, this.#i18n.errors?.session_expired || '');
      return;
    }

    const blocks = form.querySelectorAll('[data-pet-block]');
    /** @type {Record<string, string>} */
    const errors = {};

    blocks.forEach((block, index) => {
      const petIndex = index + 1;
      const zipCode =
        index === 0 ? form.querySelector('[name="zipCode"]')?.value?.toString().trim() : '';
      const petName = block.querySelector(`[name="pet${petIndex}_Name"]`)?.value?.toString().trim();
      const petType = block.querySelector(`[name="pet${petIndex}_Type"]`)?.value?.trim();
      const petStage = block.querySelector(`[name="pet${petIndex}_Stage"]`)?.value?.trim();
      const petBirthday = block
        .querySelector(`[name="pet${petIndex}_Birthday"]`)
        ?.value?.toString()
        .trim();

      if (index === 0 && !zipCode) {
        errors.zipCode = this.#errorMessage('zipCode');
      }
      if (!petName) {
        errors[`pet${petIndex}_Name`] = this.#errorMessage(`pet${petIndex}_Name`);
      }
      if (!petType) {
        errors[`pet${petIndex}_Type`] = this.#errorMessage(`pet${petIndex}_Type`);
      }
      if (!petStage) {
        errors[`pet${petIndex}_Stage`] = this.#errorMessage(`pet${petIndex}_Stage`);
      }
      if (!petBirthday) {
        errors[`pet${petIndex}_Birthday`] = this.#errorMessage(`pet${petIndex}_Birthday`);
      }
    });

    if (Object.keys(errors).length > 0) {
      this.#renderErrors(form, errors);
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    this.#setSubmitting(submitButton, true);

    const formData = new FormData(form);
    const properties = buildStepTwoProperties(formData);

    try {
      const consented = await whenMarketingConsent();
      if (!consented) {
        throw new Error(this.#i18n.errors?.consent_required || this.#i18n.errors?.generic || '');
      }

      await updateProfileProperties(email, properties);
      this.#goToThankYou();
    } catch (error) {
      this.#renderApiError(
        form,
        error instanceof Error ? error.message : this.#i18n.errors?.generic || ''
      );
    } finally {
      this.#setSubmitting(submitButton, false);
    }
  }

  #goToThankYou() {
    const url = this.dataset.thankYouUrl;
    if (!url) return;

    sessionStorage.removeItem(SESSION_STEP);
    window.location.assign(url);
  }

  /**
   * @param {Element | null} button
   * @param {boolean} isSubmitting
   */
  #setSubmitting(button, isSubmitting) {
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = isSubmitting;

    const label = button.querySelector('[data-submit-label]');
    const spinner = button.querySelector('[data-submit-spinner]');

    if (label instanceof HTMLElement) {
      label.hidden = isSubmitting;
    }
    if (spinner instanceof HTMLElement) {
      spinner.hidden = !isSubmitting;
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  #clearErrors(form) {
    form.querySelectorAll('[data-field-error]').forEach((node) => {
      node.textContent = '';
      node.hidden = true;
    });

    const apiError = form.querySelector('[data-api-error]');
    if (apiError instanceof HTMLElement) {
      apiError.hidden = true;
      apiError.textContent = '';
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {Record<string, string>} errors
   */
  #renderErrors(form, errors) {
    for (const [key, message] of Object.entries(errors)) {
      const errorEl = form.querySelector(`[data-field-error="${key}"]`);
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #renderApiError(form, message) {
    const apiError = form.querySelector('[data-api-error]');
    if (!(apiError instanceof HTMLElement)) return;
    apiError.textContent = message;
    apiError.hidden = false;
  }
}

if (!customElements.get('email-opt-form')) {
  customElements.define('email-opt-form', EmailOptForm);
}
